import { create } from 'zustand'
import type {
  InterviewConfig,
  InterviewPhase,
  InterviewSegment,
  InterviewStatus,
  SegmentTranscript,
} from '@/types/interview'

interface InterviewState {
  status: InterviewStatus
  // True when the session reached 'complete' via a manual abort (end button)
  // rather than a natural finish. Drives the post-interview destination.
  aborted: boolean
  config: InterviewConfig | null
  sessionId: string | null
  livekitUrl: string | null
  livekitToken: string | null
  avatarEnabled: boolean
  // True once the BEY avatar's video track is actually attached to the
  // <video> element (i.e. the candidate is seeing the interviewer). Used to
  // gate the Start Interview button + render the loading overlay. Stays
  // false when avatarEnabled is false (no avatar to wait for in that mode).
  avatarReady: boolean
  // True when the backend told us BEY is at capacity (issue #22 Phase 1) or
  // BEY rejected the connection for any other reason. The candidate sees a
  // "no interviewer available" overlay and the Start button stays disabled;
  // they can refresh to retry.
  avatarUnavailable: boolean
  // True when BEY refused to start after our bounded retry budget was
  // exhausted (reason='bey_429_persistent'). The worker is alive and
  // running in audio-only fallback mode — the candidate sees a "no avatar,
  // continue without it?" overlay with a Continue button. Distinct from
  // avatarUnavailable because that one is a dead-end; this one isn't.
  avatarDegraded: boolean
  // Queue state (issue #22 Phase 2). When the worker is waiting for a BEY
  // slot it pushes its FIFO position via the queue_status data message
  // (with the queue-status endpoint as a pull fallback). queuePosition is
  // 0-based; null means "not currently queued". The Start button stays
  // disabled while queued, and AvatarVideo renders a "you're in line" overlay.
  queuePosition: number | null
  queueEtaSeconds: number | null
  queueDepth: number | null
  segments: string[]
  currentSegment: InterviewSegment | null
  currentPhase: InterviewPhase
  agentMessage: string
  realtimeTranscript: string
  transcripts: SegmentTranscript[]
  recordingElapsed: number
  cleanedTranscript: string | null
  // Why getUserMedia failed, as a language-neutral code the UI maps to a
  // localized banner ('permission_denied' | 'no_device' | 'device_busy' |
  // 'unknown'). null = no media error. Cleared on every new start attempt.
  mediaError: MediaErrorCode | null

  setStatus: (s: InterviewStatus) => void
  setAborted: (aborted: boolean) => void
  setConfig: (c: InterviewConfig) => void
  setSession: (id: string, url: string, token: string, avatarEnabled: boolean) => void
  setAvatarReady: (ready: boolean) => void
  setAvatarUnavailable: (unavailable: boolean) => void
  setAvatarDegraded: (degraded: boolean) => void
  setQueueStatus: (position: number | null, etaSeconds: number | null, depth: number | null) => void
  setSegments: (segments: string[]) => void
  setSegment: (s: InterviewSegment | null) => void
  setPhase: (p: InterviewPhase) => void
  setAgentMessage: (msg: string) => void
  setRealtimeTranscript: (t: string) => void
  addTranscript: (t: SegmentTranscript) => void
  updateTranscript: (segment: InterviewSegment, text: string) => void
  setRecordingElapsed: (n: number) => void
  setCleanedTranscript: (text: string | null) => void
  setMediaError: (code: MediaErrorCode | null) => void
  reset: () => void
}

export type MediaErrorCode = 'permission_denied' | 'no_device' | 'device_busy' | 'unknown'

const initialState = {
  status: 'idle' as InterviewStatus,
  aborted: false,
  config: null,
  sessionId: null,
  livekitUrl: null,
  livekitToken: null,
  avatarEnabled: false,
  avatarReady: false,
  avatarUnavailable: false,
  avatarDegraded: false,
  queuePosition: null as number | null,
  queueEtaSeconds: null as number | null,
  queueDepth: null as number | null,
  segments: [] as string[],
  currentSegment: null,
  currentPhase: 'waiting' as InterviewPhase,
  agentMessage: '',
  realtimeTranscript: '',
  transcripts: [] as SegmentTranscript[],
  recordingElapsed: 0,
  cleanedTranscript: null,
  mediaError: null as MediaErrorCode | null,
}

export const useInterviewStore = create<InterviewState>((set) => ({
  ...initialState,

  setStatus: (status) => set({ status }),
  setAborted: (aborted) => set({ aborted }),
  setConfig: (config) => set({ config }),
  setSession: (sessionId, livekitUrl, livekitToken, avatarEnabled) =>
    set({ sessionId, livekitUrl, livekitToken, avatarEnabled }),
  setAvatarReady: (avatarReady) => set({ avatarReady }),
  setAvatarUnavailable: (avatarUnavailable) => set({ avatarUnavailable }),
  setAvatarDegraded: (avatarDegraded) => set({ avatarDegraded }),
  setQueueStatus: (queuePosition, queueEtaSeconds, queueDepth) =>
    set({ queuePosition, queueEtaSeconds, queueDepth }),
  setSegments: (segments) => set({ segments }),
  setSegment: (currentSegment) => set({ currentSegment }),
  setPhase: (currentPhase) => set({ currentPhase }),
  setAgentMessage: (agentMessage) => set({ agentMessage }),
  setRealtimeTranscript: (realtimeTranscript) => set({ realtimeTranscript }),
  addTranscript: (t) => set((s) => ({ transcripts: [...s.transcripts, t] })),
  updateTranscript: (segment, text) =>
    set((s) => ({
      transcripts: s.transcripts.map((t) => (t.segment === segment ? { ...t, text } : t)),
    })),
  setRecordingElapsed: (recordingElapsed) => set({ recordingElapsed }),
  setCleanedTranscript: (cleanedTranscript) => set({ cleanedTranscript }),
  setMediaError: (mediaError) => set({ mediaError }),
  reset: () => set(initialState),
}))
